import LoadingButton from "@mui/lab/LoadingButton";
import {
  Box,
  Card,
  CardMedia,
  Skeleton,
  Stack,
  TextField,
  Typography,
  alpha,
  useTheme,
} from "@mui/material";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import axios from "axios";
import React, { useState } from "react";
import toast from "react-hot-toast";
import { useSelector } from "react-redux";
import { RootState } from "../../../../core/store/store";

type ImageData = {
  img: string;
  title: string;
  author: string;
};

interface AIImageTabProps {
  onSelectImage: (url: string, attribution?: string) => void;
}

export const AIImageTab: React.FC<AIImageTabProps> = ({ 
  onSelectImage 
}) => {
  const [prompt, setPrompt] = useState("");
  const [loading, setLoading] = useState(false);
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [imageLoading, setImageLoading] = useState(false);
  const { apiUrl } = useSelector((state: RootState) => state.home);
  const theme = useTheme();

  const handleGenerate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!prompt) return;

    setLoading(true);
    setImageUrl(null);
    try {
      const response = await axios.post(`${apiUrl}/article/search-images`, {
        library: "AI Images",
        search: prompt,
      });
      const result = response.data.result as ImageData[];
      if (result.length > 0) {
        setImageUrl(result[0].img);
        setImageLoading(true);
        onSelectImage(result[0].img, "");
      }
    } catch (error: any) {
      toast.error(error.response?.data?.error?.message || "Failed to generate image");
    } finally {
      setLoading(false);
    }
  };

  const useTemplate = () => {
    setPrompt(`Create a highly detailed image of [subject/concept], designed for [purpose/audience]. Avoid using [specific undesired elements or styles], and emphasize [specific visual characteristics or details, such as color schemes, textures, lighting, perspective, or mood]. The image should evoke [emotion or atmosphere] and include [optional: any specific elements, objects, or scenery].`);
  };

  return (
    <Stack spacing={3}>
      <Typography variant="body2" color="text.secondary">
        Create unique images with AI based on your description.
      </Typography>

      <form onSubmit={handleGenerate}>
        <Stack spacing={2}>
          {!imageUrl && !loading && (
            <Box
              sx={{
                p: 2,
                bgcolor: alpha(theme.palette.info.main, 0.05),
                borderRadius: 2,
                border: `1px solid ${alpha(theme.palette.info.main, 0.2)}`,
                cursor: 'pointer',
                '&:hover': {
                  bgcolor: alpha(theme.palette.info.main, 0.08),
                }
              }}
              onClick={useTemplate}
            >
              <Stack direction="row" alignItems="center" spacing={1}>
                <AutoAwesomeIcon sx={{ fontSize: '1rem', color: theme.palette.info.main }} />
                <Typography variant="subtitle2" color="info.main">
                  Use Advanced Template
                </Typography>
              </Stack>
              <Typography variant="caption" color="text.secondary">
                Click to fill the prompt with a detailed template
              </Typography>
            </Box>
          )}

          <TextField
            variant="outlined"
            placeholder={`Describe the image you want to create...

Example: "A professional business meeting in a modern office with natural lighting, showing diverse team members collaborating around a whiteboard"`}
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            fullWidth
            multiline
            rows={4}
            disabled={loading}
            inputProps={{ maxLength: 4000 }}
            helperText={`${prompt.length}/4000 characters`}
          />

          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <LoadingButton
              variant="contained"
              type="submit"
              disabled={!prompt || loading}
              loading={loading}
              startIcon={!loading && <AutoAwesomeIcon />}
              size="large"
              sx={{ px: 4 }}
            >
              Generate AI Image
            </LoadingButton>
          </Box>
        </Stack>
      </form>

      {imageUrl && (
        <Card sx={{ position: 'relative', mt: 2 }}>
          {imageLoading && (
            <Box sx={{ p: 3 }}>
              <Skeleton variant="rectangular" height={400} />
            </Box>
          )}
          <Box sx={{ p: imageLoading ? 0 : 3 }}>
            <CardMedia
              component="img"
              image={imageUrl}
              alt="Generated AI Image"
              onLoad={() => setImageLoading(false)}
              onError={() => {
                setImageLoading(false);
                toast.error("Failed to load generated image");
              }}
              sx={{
                display: imageLoading ? 'none' : 'block',
                maxHeight: 400,
                objectFit: 'contain',
                borderRadius: 1,
              }}
            />
          </Box>
          <Box sx={{ p: 2, bgcolor: alpha(theme.palette.success.main, 0.1) }}>
            <Typography variant="body2" color="success.main">
              ✓ AI image generated successfully!
            </Typography>
          </Box>
        </Card>
      )}
    </Stack>
  );
};